import {
  DestinationPropertyMetadata,
  getPropertyGroupMetadata,
  getPropertySensitivityLevelsForDestination,
  isPropertyAllowed,
  isPropertyMaskedForDestination,
  PropertyGroup,
} from "@/destinations/DestinationPropertyMetadata";
import { DestinationKey } from "@/destinations/types";
import { AllowListType } from "@/PHIAllowListPage/types";
import { LocalStorageProject } from "@/reducers/environments";
import {
  LoadedDestinationInfo,
  PropertySensitivityLevels,
  SensitivityLevel,
} from "@/types/global";

const REQUIRED_GROUPS = [
  PropertyGroup.Required,
  PropertyGroup.RequiredWeb,
  PropertyGroup.RequiredIOS,
  PropertyGroup.RequiredAndroid,
];

export type AllowlistCandidate = DestinationPropertyMetadata & {
  requirement: "required" | "recommended";
};

export function allowlistCandidateKey(candidate: AllowlistCandidate): string {
  return `${candidate.propertyType}:${candidate.property}`;
}

/** The properties a destination calls out as required or recommended. */
export function getAllowlistCandidates(
  destinationKey: DestinationKey,
): AllowlistCandidate[] {
  const required = REQUIRED_GROUPS.flatMap((group) =>
    getPropertyGroupMetadata(destinationKey, group),
  ).map((p) => ({ ...p, requirement: "required" as const }));
  const recommended = getPropertyGroupMetadata(
    destinationKey,
    PropertyGroup.Recommended,
  ).map((p) => ({ ...p, requirement: "recommended" as const }));

  const seen = new Set<string>();
  return [...required, ...recommended].filter((candidate) => {
    const key = allowlistCandidateKey(candidate);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/** "hashed" properties are sent masked, so allowing them here would unmask them. */
export type AllowlistStatus = "allowed" | "hashed" | "notAllowed";

function projectSensitivityLevels(
  project: LocalStorageProject,
  propertyType: AllowListType,
): PropertySensitivityLevels {
  switch (propertyType) {
    case AllowListType.Event:
      return project.eventPropertySensitivityLevels;
    case AllowListType.User:
      return project.userPropertySensitivityLevels;
    case AllowListType.Group:
      return project.groupPropertySensitivityLevels;
  }
}

/** Ingest only falls back to the project's level when the destination sets none. */
function isMaskedByProjectOnly(
  candidate: AllowlistCandidate,
  project: LocalStorageProject,
  destination: LoadedDestinationInfo,
): boolean {
  const names = [candidate.property, candidate.alternateName].filter(
    (name): name is string => !!name,
  );
  const destinationLevels = getPropertySensitivityLevelsForDestination(
    destination,
    candidate.propertyType,
  );
  if (
    names.some(
      (name) =>
        destinationLevels[name] !== undefined &&
        destinationLevels[name] !== SensitivityLevel.NoSensitivitySet,
    )
  ) {
    return false;
  }
  const projectLevels =
    projectSensitivityLevels(project, candidate.propertyType) ?? {};
  return names.some(
    (name) => projectLevels[name] === SensitivityLevel.NotSensitiveIfHashed,
  );
}

export function getAllowlistStatus(
  candidate: AllowlistCandidate,
  project: LocalStorageProject,
  destination: LoadedDestinationInfo,
): AllowlistStatus {
  if (isPropertyAllowed(candidate, project, destination)) {
    return "allowed";
  }
  if (
    isPropertyMaskedForDestination(candidate, destination) ||
    isMaskedByProjectOnly(candidate, project, destination)
  ) {
    return "hashed";
  }
  return "notAllowed";
}

export function isCandidateSelected(
  candidate: AllowlistCandidate,
  selectionOverrides: Record<string, boolean>,
): boolean {
  return (
    selectionOverrides[allowlistCandidateKey(candidate)] ??
    candidate.requirement === "required"
  );
}

/**
 * Turn-on needs required properties sent unmasked, so a required property that
 * is only allowed hashed can be unmasked here, but only when the user opts in.
 */
export function canUnmask(
  candidate: AllowlistCandidate,
  status: AllowlistStatus,
): boolean {
  return status === "hashed" && candidate.requirement === "required";
}

/** Selected properties that aren't allowlisted yet, plus hashed ones the user chose to unmask. */
export function getPropertiesToAllow({
  candidates,
  project,
  destination,
  selectionOverrides,
}: {
  candidates: AllowlistCandidate[];
  project: LocalStorageProject;
  destination: LoadedDestinationInfo;
  selectionOverrides: Record<string, boolean>;
}): AllowlistCandidate[] {
  return candidates.filter((candidate) => {
    const status = getAllowlistStatus(candidate, project, destination);
    if (canUnmask(candidate, status)) {
      return selectionOverrides[allowlistCandidateKey(candidate)] === true;
    }
    return (
      status === "notAllowed" &&
      isCandidateSelected(candidate, selectionOverrides)
    );
  });
}

/**
 * Allows the selected properties unmasked on the destination's existing
 * allowlists. Never removes an entry.
 */
export function buildAllowlistUpdate(
  destination: LoadedDestinationInfo,
  selected: DestinationPropertyMetadata[],
): Record<AllowListType, PropertySensitivityLevels> {
  const levels: Record<AllowListType, PropertySensitivityLevels> = {
    [AllowListType.Event]: {
      ...getPropertySensitivityLevelsForDestination(
        destination,
        AllowListType.Event,
      ),
    },
    [AllowListType.User]: {
      ...getPropertySensitivityLevelsForDestination(
        destination,
        AllowListType.User,
      ),
    },
    [AllowListType.Group]: {
      ...getPropertySensitivityLevelsForDestination(
        destination,
        AllowListType.Group,
      ),
    },
  };

  for (const property of selected) {
    levels[property.propertyType][property.property] =
      SensitivityLevel.NotSensitive;
  }
  return levels;
}
